import gql from 'graphql-tag';
import { SignInData } from '../../Services/Auth/types';
import { UserSchema } from '../../definitions/schemas';
import Interface from '../Interface';

interface SignInInput {
  captchaAnswer?: string;
  email?: string;
  ott?: string;
  password?: string;
}

interface SignUpInput {
  captchaAnswer?: string;
  email?: string;
  firstName?: string;
  lastName?: string;
  password?: string;
  subscribedToMarketing?: boolean;
}

interface SignUpData {
  user: UserSchema;
}

interface ForgotPasswordData {
  sent: boolean;
}

interface ForgotPasswordInput {
  captchaAnswer: string;
  email: string;
}

interface ResetPasswordInput {
  password: string;
  token: string;
}

interface ResetPasswordData {
  token: string;
}

interface SignInTokenInput {
  userId: string;
}

interface SignInTokenData {
  token: string;
}

export default class AuthInterface extends Interface {
  public async signIn(input: SignInInput) {
    return this.mutate<SignInData>(async () => {
      const res = await this.apolloService.executeMutation({
        mutation: gql`
          mutation signIn($input: SignInInput!) {
            signIn(input: $input) {
              accessToken
              tokenType
              nextPath
            }
          }
        `,
        variables: {
          input,
        },
      });

      return {
        data: res.data.signIn,
        serverErrors: res.errors,
        serverMessage: res.errors?.map((e) => e.message).join(''),
      };
    }, 'Error signing in');
  }

  public async signUp(input: SignUpInput) {
    return this.mutate<SignUpData>(async () => {
      const res = await this.apolloService.executeMutation({
        mutation: gql`
          mutation signUp($input: SignUpInput!) {
            signUp(input: $input) {
              user {
                email
              }
            }
          }
        `,
        variables: {
          input,
        },
      });

      return {
        data: res.data.signUp,
        serverErrors: res.errors,
        serverMessage: res.errors?.map((e) => e.message).join(''),
      };
    }, 'Error signing up');
  }

  public async signOut() {
    return this.mutate<null>(async () => {
      await this.apolloService.executeMutation({
        mutation: gql`
          mutation signOut {
            signOut {
              success
            }
          }
        `,
      });

      return {
        data: null,
      };
    }, 'Error signing out');
  }

  public async forgotPassword(input: ForgotPasswordInput) {
    return this.mutate<ForgotPasswordData>(async () => {
      const res = await this.apolloService.executeMutation({
        mutation: gql`
          mutation forgotPassword($input: ForgotPasswordInput!) {
            forgotPassword(input: $input) {
              sent
            }
          }
        `,
        variables: {
          input,
        },
      });

      return {
        data: res.data.forgotPassword,
        serverErrors: res.errors,
        serverMessage: res.errors?.map((e) => e.message).join(''),
      };
    }, 'Error signing out');
  }

  public async resetPassword(input: ResetPasswordInput) {
    return this.mutate<ResetPasswordData>(async () => {
      const res = await this.apolloService.executeMutation({
        mutation: gql`
          mutation resetPassword($input: ResetPasswordInput!) {
            resetPassword(input: $input) {
              token
            }
          }
        `,
        variables: {
          input,
        },
      });

      return {
        data: res.data.resetPassword,
        serverErrors: res.errors,
        serverMessage: res.errors?.map((e) => e.message).join(''),
      };
    }, 'Error signing out');
  }

  public async signInToken(input: SignInTokenInput) {
    return this.mutate<SignInTokenData>(async () => {
      const res = await this.apolloService.executeMutation({
        mutation: gql`
          mutation signInToken($input: SignInTokenInput!) {
            signInToken(input: $input) {
              token
            }
          }
        `,
        variables: {
          input,
        },
      });

      return {
        data: res.data.signInToken,
        serverErrors: res.errors,
        serverMessage: res.errors?.map((e) => e.message).join(''),
      };
    }, 'Error signing out');
  }
}
